import React, { useMemo, useState } from 'react';

import { useSelector } from 'react-redux';
import { validate as isValidUUID } from 'uuid';

import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';
import { DesktopDateTimePicker } from '@mui/x-date-pickers-pro';

import { useListUserCarts } from '~/hooks/apis/carts/useListUserCarts';
import useQuery from '~/hooks/useQuery';

import { Dayjs } from '~/services/TimeService';

import { CartType, GetUserCartsParams } from '~/types/services/carts';

import CartsTable from './CartsTable';
import StatusesHeaderTooltip from './StatusesHeaderTooltip';

const STATUS_OPTIONS = Object.values(CartType);

const DEFAULT_PAGE_SIZE = 10;

interface Props {
  userId: string;
}

export default function CartsTab({ userId }: Props) {
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const urlSearchParams = useQuery();

  const [cartIdInput, setCartIdInput] = useState(() => urlSearchParams.get('cartId') ?? '');
  const [createdAtFrom, setCreatedAtFrom] = useState<Dayjs | null>(null);
  const [createdAtTo, setCreatedAtTo] = useState<Dayjs | null>(null);
  const [statuses, setStatuses] = useState<Array<CartType>>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortDir, setSortDir] = useState<'ASC' | 'DESC'>('DESC');

  const trimmedCartId = cartIdInput.trim();
  const isCartIdValid = trimmedCartId === '' || isValidUUID(trimmedCartId);
  const cartIdError = !isCartIdValid ? 'Cart ID must be a valid UUID' : undefined;

  const params = useMemo<GetUserCartsParams>(
    () => ({
      brand,
      sortDir,
      page,
      limit: pageSize,
      ...(isCartIdValid && trimmedCartId ? { cartId: trimmedCartId } : {}),
      ...(createdAtFrom ? { createdAtFrom: createdAtFrom.utc().toISOString() } : {}),
      ...(createdAtTo ? { createdAtTo: createdAtTo.utc().toISOString() } : {}),
      ...(statuses.length ? { statuses } : {}),
    }),
    [brand, sortDir, page, pageSize, isCartIdValid, trimmedCartId, createdAtFrom, createdAtTo, statuses],
  );

  const { data, isLoading, isFetching, error } = useListUserCarts(userId, params, {
    enabled: !!userId && !!brand && isCartIdValid,
  });

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="flex-start" flexWrap="wrap" useFlexGap>
        <TextField
          label="Search by Cart ID"
          placeholder="Enter cart UUID"
          value={cartIdInput}
          onChange={(event) => setCartIdInput(event.target.value)}
          error={!!cartIdError}
          helperText={cartIdError}
          sx={{ width: 360 }}
          size="small"
        />
        <DesktopDateTimePicker
          label="Created from"
          value={createdAtFrom}
          onChange={setCreatedAtFrom}
          ampm={false}
          slotProps={{ textField: { size: 'small', sx: { width: 240 } }, field: { clearable: true } }}
        />
        <DesktopDateTimePicker
          label="Created to"
          value={createdAtTo}
          onChange={setCreatedAtTo}
          ampm={false}
          slotProps={{ textField: { size: 'small', sx: { width: 240 } }, field: { clearable: true } }}
        />
        <FormControl size="small" sx={{ width: 240 }}>
          <InputLabel>Statuses</InputLabel>
          <Select
            multiple
            label="Statuses"
            value={statuses}
            onChange={(event) => {
              const value = event.target.value;
              setStatuses(typeof value === 'string' ? (value.split(',') as Array<CartType>) : value);
            }}
            renderValue={(selected) => selected.join(', ')}
          >
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                <Checkbox checked={statuses.includes(status)} />
                <ListItemText primary={status} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* Tooltip for statuses header */}
        <StatusesHeaderTooltip />
      </Stack>

      {error && (
        <Box mt={2}>
          <Alert severity="error">Failed to load carts: {error.message}</Alert>
        </Box>
      )}

      <Box mt={2}>
        <CartsTable
          rows={data?.result ?? []}
          total={data?.total ?? 0}
          page={page}
          pageSize={pageSize}
          sortDir={sortDir}
          loading={isLoading || isFetching}
          onPaginationChange={(nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize);
          }}
          onSortChange={setSortDir}
        />
      </Box>
    </Box>
  );
}
